export type PermissionTuple = Readonly<{
  domain: string;
  action: string;
  resource: string;
}>;

/**
 * Canonical tuple constants for authorization checks.
 * Naming policy:
 * - key: UPPER_SNAKE_CASE feature/action descriptor
 * - tuple: backend-aligned UPPER_SNAKE_CASE values
 */
export const DOMAINS = {
  CUSTOMERS: "CUSTOMERS",
  ONBOARDING: "ONBOARDING",
  SCREENING: "SCREENING",
  COMPLIANCE: "COMPLIANCE",
  RBAC: "RBAC",
  ADMIN: "ADMIN",
  DASHBOARD: "DASHBOARD",
} as const;

export const ACTIONS = {
  READ: "READ",
  WRITE: "WRITE",
  UPDATE: "UPDATE",
  EXECUTE: "EXECUTE",
  EXPORT: "EXPORT",
  CLOSE: "CLOSE",
  APPROVE: "APPROVE",
  APPROVE_L1: "APPROVE_L1",
  APPROVE_L2: "APPROVE_L2",
  PUBLISH: "PUBLISH",
  ASSIGN: "ASSIGN",
  REVOKE: "REVOKE",
  DEROGATE: "DEROGATE",
  DECIDE: "DECIDE",
  CONFIGURE: "CONFIGURE",
  INSTRUCT: "INSTRUCT",
  MANAGE: "MANAGE",
  ALL: "*",
} as const;

export const RBAC_RESOURCES = {
  ROLES: "ROLES",
  /** Backend inventory: publish-role formal approval (`PUBLISH` + singular `ROLE`). */
  ROLE: "ROLE",
  USER: "USER",
  USERS: "USERS",
  PERMISSIONS_MATRIX: "PERMISSIONS_MATRIX",
  SOD_WARN: "SOD_WARN",
  ALL: "*",
} as const;

export const ADMIN_RESOURCES = {
  SYSTEM_CONFIG: "SYSTEM_CONFIG",
  AUDIT_LOGS: "AUDIT_LOGS",
  REKYC_CASES: "REKYC_CASES",
  ALERTS: "ALERTS",
  RISK: "RISK",
  RESULTS: "RESULTS",
  ALL: "*",
} as const;

export const COMPLIANCE_RESOURCES = {
  RISK_ENTITY: "RISK_ENTITY",
  L1_REVIEW: "L1_REVIEW",
  P4_VALIDATION: "P4_VALIDATION",
  EDD: "EDD",
  EDD_L1: "EDD_L1",
  KYB_CASE: "KYB_CASE",
  KYB_L1: "KYB_L1",
  KYB_CORPORATE_ACCESS: "KYB_CORPORATE_ACCESS",
  KYB_CORPORATE_ACCESS_PROFILE: "KYB_CORPORATE_ACCESS_PROFILE",
  KYB_ACCESS_RECERTIFICATION: "KYB_ACCESS_RECERTIFICATION",
  KYB_MONITORING: "KYB_MONITORING",
  KYB_ACCESS_MONITORING: "KYB_ACCESS_MONITORING",
  KYB_RETENTION: "KYB_RETENTION",
  KYB_DOCUMENT_MATRIX: "KYB_DOCUMENT_MATRIX",
  KYB_COUNTRY_SCOPE: "KYB_COUNTRY_SCOPE",
  KYB_LEGAL_FORM: "KYB_LEGAL_FORM",
  KYB_REF_VERSION: "KYB_REF_VERSION",
  KYB_EVIDENCE_PACK: "KYB_EVIDENCE_PACK",
} as const;

export const SCORING_RESOURCES = {
  ENTITY_REFERENTIAL: "ENTITY_REFERENTIAL",
  COMPOSITOR_CONFIG: "COMPOSITOR_CONFIG",
} as const;

export const DASHBOARD_RESOURCES = {
  /** Persona-scoped dashboards (strict 1:1 mapping, Keycloak client `DASHBOARD`). */
  P1: "P1",
  P2: "P2",
  P3: "P3",
  P4: "P4",
} as const;

/**
 * Complete permission tuple catalog currently used as frontend kernel.
 * Keep this map aligned with backend inventory and normalize singular/plural
 * mismatches here instead of introducing runtime aliases.
 */
export const FEATURE_PERMISSIONS = {
  // Dashboard — persona-scoped KPI dashboards (Keycloak client `DASHBOARD`).
  // Backend tuples: {domaine:DASHBOARD, action:READ, ressource:P1..P4}.
  DASHBOARD_P1_READ: {
    domain: DOMAINS.DASHBOARD,
    action: ACTIONS.READ,
    resource: DASHBOARD_RESOURCES.P1,
  },
  DASHBOARD_P2_READ: {
    domain: DOMAINS.DASHBOARD,
    action: ACTIONS.READ,
    resource: DASHBOARD_RESOURCES.P2,
  },
  DASHBOARD_P3_READ: {
    domain: DOMAINS.DASHBOARD,
    action: ACTIONS.READ,
    resource: DASHBOARD_RESOURCES.P3,
  },
  DASHBOARD_P4_READ: {
    domain: DOMAINS.DASHBOARD,
    action: ACTIONS.READ,
    resource: DASHBOARD_RESOURCES.P4,
  },

  // RBAC actions required by phase 02
  READ_ROLES: {
    domain: DOMAINS.RBAC,
    action: ACTIONS.READ,
    resource: RBAC_RESOURCES.ROLES,
  },
  WRITE_ROLES: {
    domain: DOMAINS.RBAC,
    action: ACTIONS.WRITE,
    resource: RBAC_RESOURCES.ROLES,
  },
  PUBLISH_ROLE: {
    domain: DOMAINS.RBAC,
    action: ACTIONS.PUBLISH,
    resource: RBAC_RESOURCES.ROLE,
  },
  ASSIGN_ROLES: {
    domain: DOMAINS.RBAC,
    action: ACTIONS.ASSIGN,
    resource: RBAC_RESOURCES.ROLES,
  },
  REVOKE_ROLES: {
    domain: DOMAINS.RBAC,
    action: ACTIONS.REVOKE,
    resource: RBAC_RESOURCES.ROLES,
  },
  READ_USER: {
    domain: DOMAINS.RBAC,
    action: ACTIONS.READ,
    resource: RBAC_RESOURCES.USER,
  },
  WRITE_USERS: {
    domain: DOMAINS.RBAC,
    action: ACTIONS.WRITE,
    resource: RBAC_RESOURCES.USERS,
  },
  READ_PERMISSIONS_MATRIX: {
    domain: DOMAINS.RBAC,
    action: ACTIONS.READ,
    resource: RBAC_RESOURCES.PERMISSIONS_MATRIX,
  },
  WRITE_PERMISSIONS_MATRIX: {
    domain: DOMAINS.RBAC,
    action: ACTIONS.WRITE,
    resource: RBAC_RESOURCES.PERMISSIONS_MATRIX,
  },
  DEROGATE_SOD_WARN: {
    domain: DOMAINS.RBAC,
    action: ACTIONS.DEROGATE,
    resource: RBAC_RESOURCES.SOD_WARN,
  },

  // Admin actions required by phase 02
  READ_SYSTEM_CONFIG: {
    domain: DOMAINS.ADMIN,
    action: ACTIONS.READ,
    resource: ADMIN_RESOURCES.SYSTEM_CONFIG,
  },
  WRITE_SYSTEM_CONFIG: {
    domain: DOMAINS.ADMIN,
    action: ACTIONS.WRITE,
    resource: ADMIN_RESOURCES.SYSTEM_CONFIG,
  },
  READ_AUDIT_LOGS: {
    domain: DOMAINS.ADMIN,
    action: ACTIONS.READ,
    resource: ADMIN_RESOURCES.AUDIT_LOGS,
  },
  EXPORT_AUDIT_LOGS: {
    domain: DOMAINS.ADMIN,
    action: ACTIONS.EXPORT,
    resource: ADMIN_RESOURCES.AUDIT_LOGS,
  },

  // Customers / onboarding / screening / compliance / reKYC examples
  CUSTOMERS_LIST_READ: {
    domain: DOMAINS.CUSTOMERS,
    action: ACTIONS.READ,
    resource: "LIST",
  },
  CUSTOMERS_PROFILE_READ: {
    domain: DOMAINS.CUSTOMERS,
    action: ACTIONS.READ,
    resource: "PROFILE",
  },
  CUSTOMERS_PROFILE_WRITE: {
    domain: DOMAINS.CUSTOMERS,
    action: ACTIONS.WRITE,
    resource: "PROFILE",
  },
  CUSTOMERS_IDENTITY_READ: {
    domain: DOMAINS.CUSTOMERS,
    action: ACTIONS.READ,
    resource: "IDENTITY",
  },
  CUSTOMERS_IDENTITY_WRITE: {
    domain: DOMAINS.CUSTOMERS,
    action: ACTIONS.WRITE,
    resource: "IDENTITY",
  },
  CUSTOMERS_COMPLIANCE_DATA_READ: {
    domain: DOMAINS.CUSTOMERS,
    action: ACTIONS.READ,
    resource: "COMPLIANCE_DATA",
  },
  CUSTOMERS_COMPLIANCE_DATA_WRITE: {
    domain: DOMAINS.CUSTOMERS,
    action: ACTIONS.WRITE,
    resource: "COMPLIANCE_DATA",
  },
  CUSTOMERS_APPROVE_ICR: {
    domain: DOMAINS.CUSTOMERS,
    action: ACTIONS.APPROVE,
    resource: "ICR",
  },
  /** Backend inventory: `ONBOARDING` + `READ|WRITE` + `CASE` (see docs/security/rbac/01-overview.md). */
  ONBOARDING_CASE_READ: {
    domain: DOMAINS.ONBOARDING,
    action: ACTIONS.READ,
    resource: "CASE",
  },
  ONBOARDING_CASE_WRITE: {
    domain: DOMAINS.ONBOARDING,
    action: ACTIONS.WRITE,
    resource: "CASE",
  },
  ONBOARDING_REVIEW_STEP_READ: {
    domain: DOMAINS.ONBOARDING,
    action: ACTIONS.READ,
    resource: "REVIEW_STEP",
  },
  ONBOARDING_REVIEW_STEP_WRITE: {
    domain: DOMAINS.ONBOARDING,
    action: ACTIONS.WRITE,
    resource: "REVIEW_STEP",
  },
  SCREENING_RESULTS_READ: {
    domain: DOMAINS.SCREENING,
    action: ACTIONS.READ,
    resource: ADMIN_RESOURCES.RESULTS,
  },
  SCREENING_RESULTS_WRITE: {
    domain: DOMAINS.SCREENING,
    action: ACTIONS.WRITE,
    resource: ADMIN_RESOURCES.RESULTS,
  },
  COMPLIANCE_RISK_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: ADMIN_RESOURCES.RISK,
  },
  COMPLIANCE_REKYC_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: "REKYC",
  },
  COMPLIANCE_REKYC_CASES_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: ADMIN_RESOURCES.REKYC_CASES,
  },
  COMPLIANCE_REKYC_CASES_WRITE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.WRITE,
    resource: ADMIN_RESOURCES.REKYC_CASES,
  },
  COMPLIANCE_REKYC_CASES_CLOSE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.CLOSE,
    resource: ADMIN_RESOURCES.REKYC_CASES,
  },
  COMPLIANCE_REKYC_CASES_APPROVE_L1: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.APPROVE_L1,
    resource: ADMIN_RESOURCES.REKYC_CASES,
  },
  COMPLIANCE_REKYC_CASES_APPROVE_L2: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.APPROVE_L2,
    resource: ADMIN_RESOURCES.REKYC_CASES,
  },
  REKYC_COMPLIANCE_DATA_READ: {
    domain: "REKYC",
    action: ACTIONS.READ,
    resource: "COMPLIANCE_DATA",
  },
  COMPLIANCE_REKYC_EXECUTE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.EXECUTE,
    resource: ADMIN_RESOURCES.REKYC_CASES,
  },
  COMPLIANCE_ALERTS_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: ADMIN_RESOURCES.ALERTS,
  },

  // Risk Entity Referential — tuples aligned with real backend (Keycloak client: SCORING).
  // Domain: SCORING | Resource: ENTITY_REFERENTIAL (confirmed in docs/US/admin-entities/endpoints 2.md)
  COMPLIANCE_RISK_ENTITY_READ: {
    domain: "SCORING",
    action: ACTIONS.READ,
    resource: SCORING_RESOURCES.ENTITY_REFERENTIAL,
  },
  COMPLIANCE_RISK_ENTITY_WRITE: {
    domain: "SCORING",
    action: ACTIONS.WRITE,
    resource: SCORING_RESOURCES.ENTITY_REFERENTIAL,
  },

  // Compositor config (GET /, GET /history, PUT /) — backend: SCORING_READ|MANAGE:COMPOSITOR_CONFIG
  COMPLIANCE_RISK_COMPOSITOR_CONFIG_READ: {
    domain: "SCORING",
    action: ACTIONS.READ,
    resource: SCORING_RESOURCES.COMPOSITOR_CONFIG,
  },
  COMPLIANCE_RISK_COMPOSITOR_CONFIG_MANAGE: {
    domain: "SCORING",
    action: ACTIONS.MANAGE,
    resource: SCORING_RESOURCES.COMPOSITOR_CONFIG,
  },

  // Risques Conformité : dossier KYB (personne morale) : lecture, soumission, instruction, validation L1.
  COMPLIANCE_KYB_CASE_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_CASE,
  },
  COMPLIANCE_KYB_CASE_WRITE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.WRITE,
    resource: COMPLIANCE_RESOURCES.KYB_CASE,
  },
  COMPLIANCE_KYB_CASE_INSTRUCT: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.INSTRUCT,
    resource: COMPLIANCE_RESOURCES.KYB_CASE,
  },
  // Accès corporate d'un mandataire : lecture et création (P10 et conformité), profil de droits (conformité).
  COMPLIANCE_KYB_CORPORATE_ACCESS_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_CORPORATE_ACCESS,
  },
  COMPLIANCE_KYB_CORPORATE_ACCESS_WRITE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.WRITE,
    resource: COMPLIANCE_RESOURCES.KYB_CORPORATE_ACCESS,
  },
  COMPLIANCE_KYB_CORPORATE_ACCESS_PROFILE_WRITE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.WRITE,
    resource: COMPLIANCE_RESOURCES.KYB_CORPORATE_ACCESS_PROFILE,
  },
  // Recertification périodique des accès corporate : la file se lit et se décide au responsable conformité (P4).
  COMPLIANCE_KYB_ACCESS_RECERTIFICATION_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_ACCESS_RECERTIFICATION,
  },
  // Tableau de suivi : bénéficiaires effectifs et EDD pour P3 et P4, accès corporate pour P4 et le CISO.
  COMPLIANCE_KYB_MONITORING_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_MONITORING,
  },
  COMPLIANCE_KYB_ACCESS_MONITORING_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_ACCESS_MONITORING,
  },
  // Paramétrage des pièces complémentaires réclamables à l'instruction : l'administration de la matrice documentaire KYB.
  COMPLIANCE_KYB_DOCUMENT_MATRIX_WRITE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.WRITE,
    resource: COMPLIANCE_RESOURCES.KYB_DOCUMENT_MATRIX,
  },
  // Périmètre du KYB : pays ouverts, formes juridiques admises, pièces exigées, versions du référentiel. Lire et écrire en brouillon ;
  // activer, désactiver et publier relèvent de la signature P4 (COMPLIANCE_P4_VALIDATION_DECIDE).
  COMPLIANCE_KYB_COUNTRY_SCOPE_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_COUNTRY_SCOPE,
  },
  COMPLIANCE_KYB_COUNTRY_SCOPE_WRITE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.WRITE,
    resource: COMPLIANCE_RESOURCES.KYB_COUNTRY_SCOPE,
  },
  COMPLIANCE_KYB_LEGAL_FORM_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_LEGAL_FORM,
  },
  COMPLIANCE_KYB_LEGAL_FORM_WRITE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.WRITE,
    resource: COMPLIANCE_RESOURCES.KYB_LEGAL_FORM,
  },
  COMPLIANCE_KYB_DOCUMENT_MATRIX_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_DOCUMENT_MATRIX,
  },
  COMPLIANCE_KYB_REF_VERSION_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_REF_VERSION,
  },
  COMPLIANCE_KYB_REF_VERSION_WRITE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.WRITE,
    resource: COMPLIANCE_RESOURCES.KYB_REF_VERSION,
  },
  // Conservation 10 ans : lecture pour P3, P4 et le CISO, décision pour le responsable conformité seul.
  COMPLIANCE_KYB_RETENTION_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_RETENTION,
  },
  COMPLIANCE_KYB_RETENTION_DECIDE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.DECIDE,
    resource: COMPLIANCE_RESOURCES.KYB_RETENTION,
  },
  // Dossier de preuve : P3, P4 et le CISO.
  COMPLIANCE_KYB_EVIDENCE_PACK_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.KYB_EVIDENCE_PACK,
  },
  COMPLIANCE_KYB_ACCESS_RECERTIFICATION_CONFIGURE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.CONFIGURE,
    resource: COMPLIANCE_RESOURCES.KYB_ACCESS_RECERTIFICATION,
  },
  COMPLIANCE_KYB_ACCESS_RECERTIFICATION_DECIDE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.DECIDE,
    resource: COMPLIANCE_RESOURCES.KYB_ACCESS_RECERTIFICATION,
  },
  COMPLIANCE_KYB_L1_DECIDE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.DECIDE,
    resource: COMPLIANCE_RESOURCES.KYB_L1,
  },
  // Risques Conformité — L1 Review (US 6.3) & P4 Validation (US 4.5)
  COMPLIANCE_L1_REVIEW_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.L1_REVIEW,
  },
  // FE-5 — supervision multi-statuts de la file L1 (P4) : lecture par statut (by-status).
  COMPLIANCE_L1_REVIEW_DECIDE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.DECIDE,
    resource: COMPLIANCE_RESOURCES.L1_REVIEW,
  },
  COMPLIANCE_P4_VALIDATION_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.P4_VALIDATION,
  },
  COMPLIANCE_P4_VALIDATION_DECIDE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.DECIDE,
    resource: COMPLIANCE_RESOURCES.P4_VALIDATION,
  },
  COMPLIANCE_EDD_READ: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.READ,
    resource: COMPLIANCE_RESOURCES.EDD,
  },
  COMPLIANCE_EDD_L1_DECIDE: {
    domain: DOMAINS.COMPLIANCE,
    action: ACTIONS.DECIDE,
    resource: COMPLIANCE_RESOURCES.EDD_L1,
  },

} as const satisfies Record<string, PermissionTuple>;

export type FeaturePermissionKey = keyof typeof FEATURE_PERMISSIONS;
